/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

import { useUiOptions } from "../ui-options-context";

type Validator = {
  validate: () => boolean;
  onInvalid: () => void;
  onSubmitted: () => void;
};

type CrossValidator = {
  shouldTriggerValidationChange: () => boolean;
  onValidationChange: () => void;
};

const focusableSelector =
  [
    "input:not([disabled]):not([type=hidden])",
    "textarea:not([disabled])",
    "select:not([disabled])",
    "button:not([disabled])",
    '[tabindex]:not([disabled]):not([tabindex="-1"])',
  ].join(":not([hidden]),") + ":not([hidden])";

const scrollAndFocusInvalidField = (form: HTMLFormElement) => {
  const invalidElement = form.querySelector<HTMLElement>(
    '[aria-invalid="true"]',
  );

  if (!invalidElement) {
    return;
  }

  invalidElement.scrollIntoView({
    block: "center",
    behavior: "smooth",
  });

  const focusTarget = invalidElement.matches(focusableSelector)
    ? invalidElement
    : invalidElement.querySelector<HTMLElement>(focusableSelector);

  focusTarget?.focus({ preventScroll: true });
};

const FormContext = createContext<
  MutableRefObject<Map<unknown, Validator>> | undefined
>(undefined);

const CrossValidationContext = createContext<
  | {
      add: (validator: CrossValidator) => void;
      remove: (validator: CrossValidator) => void;
      trigger: () => void;
    }
  | undefined
>(undefined);

/**
 * Form component that prevents submission until every field is valid.
 *
 * If multiple fields share the same state object,
 * consumers must use functional state updates to avoid race conditions.
 */
export const ExperimentalForm = ({
  onSubmit,
  children,
}: {
  /**
   * A callback function that is invoked when the form is submitted and all form fields are valid.
   */
  onSubmit: () => void;
  /**
   * Content of form
   */
  children: ReactNode;
}) => {
  const { experimental } = useUiOptions();

  const validators = useRef(new Map<unknown, Validator>());
  const formRef = useRef<HTMLFormElement>(null);
  const [invalidSubmitId, setInvalidSubmitId] = useState<{}>();
  const crossValidators = useRef(new Set<CrossValidator>());
  const [validationTick, setValidationTick] = useState(0);

  useEffect(() => {
    // Workaround: Keep cross-validation in an effect so controls with immediate commit (e.g. checkbox/switch/file)
    // are validated after state updates, not during the same event handler.
    crossValidators.current.forEach(
      ({ onValidationChange, shouldTriggerValidationChange }) => {
        if (shouldTriggerValidationChange()) {
          onValidationChange();
        }
      },
    );
  }, [validationTick]);

  useEffect(() => {
    if (!invalidSubmitId || !formRef.current) {
      return;
    }

    scrollAndFocusInvalidField(formRef.current);
  }, [invalidSubmitId]);

  const crossValidationContext = useMemo(
    () => ({
      add: (validator: CrossValidator) => {
        crossValidators.current.add(validator);
      },
      remove: (validator: CrossValidator) => {
        crossValidators.current.delete(validator);
      },
      trigger: () => setValidationTick((tick) => tick + 1),
    }),
    [],
  );

  const crossValidationContextValue = experimental?.crossValidation
    ? crossValidationContext
    : undefined;

  return (
    <form
      ref={formRef}
      css={{ display: "contents" }}
      onSubmit={(e) => {
        e.preventDefault();

        for (const validator of validators.current.values()) {
          validator.onSubmitted();
        }

        let isValid = true;

        for (const { validate, onInvalid } of validators.current.values()) {
          if (!validate()) {
            isValid = false;
            onInvalid();
          }
        }

        if (isValid) {
          onSubmit();
          return;
        }

        setInvalidSubmitId({});
      }}
    >
      <FormContext.Provider value={validators}>
        <CrossValidationContext.Provider value={crossValidationContextValue}>
          {children}
        </CrossValidationContext.Provider>
      </FormContext.Provider>
    </form>
  );
};

/**
 * Registers a field in `ExperimentalForm` and shows error before submit.
 */
export const useSubmitGuard = ({
  validate,
  onInvalid,
}: {
  /**
   * A function that returns `true` if the field is valid.
   */
  validate: () => boolean;
  /**
   * Callback the form calls when the field is invalid.
   */
  onInvalid: () => void;
}) => {
  const [id] = useState({});
  const validatorsRef = useContext(FormContext);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!validatorsRef) {
      return;
    }

    const validators = validatorsRef.current;
    const onSubmitted = () => setHasSubmitted(true);

    validators.set(id, { validate, onInvalid, onSubmitted });

    return () => {
      validators.delete(id);
    };
  }, [validate, validatorsRef, onInvalid, id]);

  return { hasSubmitted };
};

/**
 * Registers a field for cross-validation inside the nearest `ExperimentalForm`.
 */
export const useCrossValidation = (validator: CrossValidator) => {
  const crossValidationContext = useContext(CrossValidationContext);

  useEffect(() => {
    if (!crossValidationContext) {
      return () => {};
    }

    crossValidationContext.add(validator);

    return () => crossValidationContext.remove(validator);
  }, [validator, crossValidationContext]);

  return {
    triggerCrossValidation: () => {
      crossValidationContext?.trigger();
    },
  };
};

/**
 * Checks if the validation should be triggered based on the state and the submitted flag.
 */
export const shouldTriggerValidationChange =
  <
    T extends {
      errorVisible?: boolean;
      errorMessage?: string;
      invalid?: boolean;
    },
  >({
    state,
    hasSubmitted,
    handleValidate,
  }: {
    state: T;
    hasSubmitted: boolean;
    handleValidate: (state: T) => {
      invalidRules: Record<string, boolean>;
      errorMessage?: string;
    };
  }) =>
  () => {
    const { invalidRules, errorMessage } = handleValidate(state);
    const canShowError = hasSubmitted || Boolean(state.errorVisible);
    const nextInvalid = Object.values(invalidRules).some(Boolean);
    const showError =
      nextInvalid &&
      canShowError &&
      (errorMessage !== state.errorMessage || !state.errorVisible);
    const clearError =
      (Boolean(state.invalid) || Boolean(state.errorMessage)) && !nextInvalid;
    return Boolean(showError || clearError);
  };
